<template>
  <div>
    <div class="d-flex align-center justify-end mb-3">
      <v-btn class="mt-2 mr-2" small outlined @click="loadVentas" :loading="loading">Refrescar</v-btn>
    </div>

    <v-alert v-if="error" type="error" dense class="mb-3">
      {{ error }}
    </v-alert>

    <v-card v-for="v in ventas" :key="v.id" class="mb-3" outlined rounded="lg">
      <v-card-text>
        <div class="d-flex align-center justify-space-between">
          <div>
            <div class="text-subtitle-1 font-weight-medium venta-title">
            <span>{{ v.nombre }}</span>

            <div class="venta-title-actions">
              <button
                v-if="tieneCoordenadas(v)"
                type="button"
                class="map-emoji-btn"
                title="Ver ubicación en el mapa"
                @click.stop="abrirMapaVenta(v)"
              >
                🗺️
              </button>

              <button
                v-if="v.activo_id"
                type="button"
                class="history-info-btn"
                title="Ver histórico de ganancias"
                @click.stop="abrirHistoricoVenta(v)"
              >
                <span class="material-symbols-outlined history-info-icon">
                  info
                </span>
              </button>
            </div>
          </div>
            <div class="text-caption grey--text">
              Tipo: {{ v.tecnologia }} · {{ Number(v.mw || 0) }} MW
            </div>
            <div class="text-caption grey--text">
              Vendedor: <b>{{ sellerName(v) }}</b>
            </div>
            <div class="text-caption grey--text">
              Estado: <b>{{ v.estado }}</b>
              · Expira: {{ formatDateTime(v.fecha_expiracion) }}
            </div>
            <div class="mt-1">
              <v-chip x-small outlined class="mr-2">Ofertas: {{ v.num_ofertas || 0 }}</v-chip>
              <v-chip v-if="v.mi_oferta" x-small color="primary" text-color="white">
                Mi oferta: {{ money(v.mi_oferta) }}
              </v-chip>
            </div>
          </div>

          <div class="text-right sale-price-summary">
            <div class="text-caption grey--text">
              Valor semanal
            </div>

            <div class="text-subtitle-2 font-weight-medium">
              {{
                money(
                  v.precio_mercado_actual
                  || v.precio_banca
                )
              }}
            </div>

            <v-chip
              x-small
              outlined
              class="mt-1"
              :color="weeklyChangeColor(v.variacion_semanal_pct)"
            >
              {{ weeklyChangeLabel(v.variacion_semanal_pct) }}
            </v-chip>

            <div class="text-caption grey--text mt-2">
              Venta a mercado
            </div>

            <div class="font-weight-medium bank-sale-price">
              {{ money(v.precio_mercado_actual || v.precio_banca) }}
            </div>
          </div>
          </div>

       <!-- Acciones -->
      <v-row dense class="mt-3">
        <!-- OFERTAR -->
        <v-col cols="6">
          <v-btn
            block
            class="market-action-btn market-action-btn-primary"
            :disabled="!canOffer(v)"
            @click="openOffer(v)"
          >
            Hacer oferta
          </v-btn>
        </v-col>

        <!-- VER OFERTAS -->
        <v-col cols="6">
          <v-btn
            block
            outlined
            class="market-action-btn market-action-btn-secondary"
            :disabled="!isSeller(v)"
            @click="openOffersList(v)"
          >
            Ver ofertas
          </v-btn>
        </v-col>
      </v-row>

      <v-row dense class="mt-2">
        <!-- CANCELAR -->
        <v-col cols="6">
          <v-btn
            block
            outlined
            class="market-action-btn market-action-btn-cancel"
            :disabled="!canCancelSale(v)"
            @click="cancelSale(v)"
          >
            Cancelar venta
          </v-btn>
        </v-col>

        <!-- VENDER A MERCADO -->
        <v-col cols="6">
          <v-btn
            block
            class="market-action-btn market-action-btn-primary"
            :disabled="!(isSeller(v) && v.estado === 'expirada')"
            @click="sellToBank(v)"
          >
            Vender a mercado
          </v-btn>
        </v-col>
      </v-row>
      </v-card-text>
    </v-card>

    <!--  OFERTAR -->
    <v-dialog v-model="offerDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">Hacer oferta</v-card-title>
        <v-card-text v-if="selectedVenta">
          <div class="mb-2">
            <div class="text-subtitle-2 font-weight-medium">{{ selectedVenta.nombre }}</div>
            <div class="text-caption grey--text">
              Precio mercado:
              {{ money(selectedVenta.precio_mercado_actual || selectedVenta.precio_banca) }}
            </div>
          </div>

          <v-text-field
            v-model.number="offerAmount"
            type="number"
            label="Tu oferta (€)"
            outlined
            dense
            :min="1"
            :error="!!offerError"
            :error-messages="offerError"
          />
          <div class="text-caption grey--text">Tu presupuesto: {{ money(budget) }}</div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeOffer">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!canSubmitOffer" @click="submitOffer">
            Confirmar oferta
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- LISTA OFERTAS (vendedor) -->
    <v-dialog v-model="offersDialog" max-width="720">
      <v-card>
        <v-card-title class="text-h6">Ofertas recibidas</v-card-title>

        <v-card-text>
          <v-alert v-if="offersError" type="error" dense class="mb-3">
            {{ offersError }}
          </v-alert>

          <v-simple-table v-if="offers.length">
            <thead>
              <tr>
                <th>Comprador</th>
                <th>Importe</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="o in offers" :key="o.id">
                <td>{{ buyerName(o) }}</td>
                <td>{{ money(o.importe) }}</td>
                <td>{{ o.estado }}</td>
                <td>
                  <v-btn
                    x-small
                    color="primary"
                    :disabled="o.estado !== 'activa'"
                    @click="acceptOffer(o)"
                  >
                    Aceptar
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <div v-else class="text-caption grey--text">
            No hay ofertas.
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeOffersList">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!--  MAPA VENTA -->
    <v-dialog v-model="mapDialog" max-width="900">
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center justify-space-between">
          <div>
            Ubicación del activo
            <div class="text-caption grey--text">
              {{ selectedMapVenta ? selectedMapVenta.nombre : "" }}
            </div>
          </div>

          <v-btn
            icon
            class="map-close-btn"
            @click="cerrarMapaVenta"
          >
            <span class="map-close-x">×</span>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div v-if="selectedMapVenta" class="asset-map-info mb-2">
            <strong>{{ selectedMapVenta.nombre }}</strong>
            <span v-if="selectedMapVenta.tecnologia"> · {{ selectedMapVenta.tecnologia }}</span>
            <span v-if="selectedMapVenta.mw"> · {{ Number(selectedMapVenta.mw || 0) }} MW</span>
            <br />
            <span v-if="selectedMapVenta.municipio || selectedMapVenta.provincia">
              {{ selectedMapVenta.municipio || "—" }} / {{ selectedMapVenta.provincia || "—" }}
            </span>
          </div>

          <div ref="ventaMap" class="asset-map-modal"></div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- HISTÓRICO VENTA -->
      <v-dialog v-model="historyDialog" max-width="560px">
        <v-card rounded="lg">
          <v-card-title class="headline grey lighten-4">
            <span class="modal-history-title">
              Histórico de ganancias
            </span>
          </v-card-title>

          <v-card-text class="pa-4">
            <div v-if="selectedHistoryVenta" class="mb-3">
              <div class="text-subtitle-2 font-weight-medium">
                {{ selectedHistoryVenta.nombre }}
              </div>

              <div class="text-caption grey--text">
                {{ selectedHistoryVenta.tecnologia }} · {{ Number(selectedHistoryVenta.mw || 0) }} MW
              </div>
            </div>

            <v-progress-linear
              v-if="loadingHistory"
              indeterminate
              color="primary"
              class="mb-4"
            />

            <div
              v-if="!loadingHistory && assetHistory.length === 0"
              class="text-center py-4 grey--text"
            >
              Este activo todavía no tiene histórico registrado.
            </div>

            <div v-if="!loadingHistory && assetHistory.length > 0">
              <div class="text-caption grey--text mb-3">
                Últimas 5 ganancias registradas para este activo.
              </div>

              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th class="text-right">Ganancia</th>
                    <th class="text-right">Puntos</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(h, index) in assetHistory" :key="index">
                    <td>{{ formatDate(h.fecha) }}</td>

                    <td class="text-right">
                      {{ moneyDecimal(h.ganancia_activo) }}
                    </td>

                    <td class="text-right">
                      <span
                        class="asset-history-points"
                        :class="Number(h.puntos_activo || 0) >= 0 ? 'green--text' : 'red--text'"
                      >
                        {{ formatPoints(h.puntos_activo) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" text @click="historyDialog = false">
              Cerrar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  </div>
</template>

<script>
import axios from "@/axios";
import { useGameStore } from "@/stores/game";
import Swal from "sweetalert2";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default {
  name: "MarketSales",
  props: {
    budget: { type: Number, default: 0 },
    ligaId: { type: [Number, String], default: null },
  },
  data() {
    return {
      store: useGameStore(),
      ventas: [],
      loading: false,
      error: "",

      offerDialog: false,
      selectedVenta: null,
      offerAmount: null,
      offerError: "",

      offersDialog: false,
      offersVenta: null,
      offersVentaId: null,
      offers: [],
      offersError: "",
      mapDialog: false,
      selectedMapVenta: null,
      ventaMap: null,
      ventaMarker: null,

      historyDialog: false,
      selectedHistoryVenta: null,
      assetHistory: [],
      loadingHistory: false,
      historyLoadingId: null
    };
  },
  computed: {
    activeLigaId() {
      const rawLigaId =
        this.ligaId ??
        this.store.currentAreaId ??
        this.store.player?.activeLigaId ??
        this.store.company?.player?.activeLigaId ??
        this.store.authUser?.active_liga_id ??
        this.store.authUser?.activeLigaId;

      const ligaId = Number(rawLigaId);

      return Number.isInteger(ligaId) && ligaId > 0
        ? ligaId
        : null;
    },
    meId() {
      return this.store?.authUser?.id != null
        ? String(this.store.authUser.id)
        : null;
    },
    canSubmitOffer() {
      const amount = Number(this.offerAmount || 0);
      if (!this.selectedVenta) return false;
      if (!Number.isFinite(amount) || amount <= 0) return false;
      if (amount > Number(this.budget || 0)) return false;
      return true;
    },
  },
  watch: {
    async activeLigaId(newLigaId, oldLigaId) {
      if (newLigaId === oldLigaId) return;

      this.resetLeagueState();

      if (newLigaId) {
        await this.loadVentas();
        await this.store.showPendingNotifications(newLigaId);
      }
    },
  },
  async mounted() {
    if (!this.activeLigaId) return;

    await this.loadVentas();
    await this.store.showPendingNotifications(this.activeLigaId);
  },
  beforeUnmount() {
  if (this.ventaMap) {
    this.ventaMap.remove();
    this.ventaMap = null;
  }
},
  methods: {
    requireActiveLigaId() {
      const ligaId = Number(this.activeLigaId);

      if (!Number.isInteger(ligaId) || ligaId <= 0) {
        throw new Error(
          "No se ha podido identificar la liga seleccionada. Vuelve a entrar en la liga."
        );
      }

      return ligaId;
    },

    resetLeagueState() {
      this.ventas = [];
      this.error = "";
      this.closeOffer();
      this.closeOffersList();
      this.cerrarMapaVenta();
      this.historyDialog = false;
      this.selectedHistoryVenta = null;
      this.assetHistory = [];
      this.loadingHistory = false;
      this.historyLoadingId = null;
      this.$emit("sales-count", 0);
    },

    emitBudgetRefresh() {
      this.$emit("refresh-budget", this.requireActiveLigaId());
    },

    weeklyChangeColor(value) {
  const n = Number(value || 0);

  if (n > 0) return "green";
  if (n < 0) return "red";

  return "grey";
},

weeklyChangeLabel(value) {
  const n = Number(value || 0);

  if (n > 0) {
    return `↑ ${n.toLocaleString("es-ES", {
      maximumFractionDigits: 2,
    })}%`;
  }

  if (n < 0) {
    return `↓ ${Math.abs(n).toLocaleString("es-ES", {
      maximumFractionDigits: 2,
    })}%`;
  }

  return "Sin cambios";
},
    moneyDecimal(v) {
  return `${Number(v || 0).toLocaleString("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })} €`;
},

formatDate(v) {
  if (!v) return "-";

  const d = new Date(v);

  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
},

formatPoints(v) {
  const n = Number(v || 0);

  if (n > 0) return `+${n}`;

  return `${n}`;
},

async abrirHistoricoVenta(v) {
  const activoId = v?.activo_id;

  if (!activoId) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el histórico",
      text: "Esta venta no tiene identificado el activo.",
      confirmButtonText: "Entendido",
      customClass: {
        confirmButton: "clausulazo-swal-confirm",
      },
    });
    return;
  }

  this.selectedHistoryVenta = v;
  this.historyDialog = true;
  this.loadingHistory = true;
  this.historyLoadingId = activoId;
  this.assetHistory = [];

  try {
    const ligaId = this.requireActiveLigaId();

    this.assetHistory = await this.store.fetchAssetHistory(
      activoId,
      ligaId
    );
  } catch (e) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el histórico",
      text: e.message || "Ha ocurrido un error inesperado.",
      confirmButtonText: "Entendido",
      customClass: {
        confirmButton: "clausulazo-swal-confirm",
      },
    });
  } finally {
    this.loadingHistory = false;
    this.historyLoadingId = null;
  }
},
  canCancelSale(v) {
  if (!this.isSeller(v)) return false;
  return ["abierta", "expirada"].includes(v.estado);
},
    money(v) {
      try {
        return new Intl.NumberFormat("es-ES", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(v);
      } catch (e) {
        return `${v}€`;
      }
    },
    tieneCoordenadas(v) {
  const x = Number(v.x);
  const y = Number(v.y);

  return Number.isFinite(x) && Number.isFinite(y);
},

  abrirMapaVenta(v) {
    if (!this.tieneCoordenadas(v)) return;

    this.selectedMapVenta = v;
    this.mapDialog = true;

    this.$nextTick(() => {
      setTimeout(() => {
        this.pintarMapaVenta();
      }, 250);
    });
  },

  cerrarMapaVenta() {
    this.mapDialog = false;
    this.selectedMapVenta = null;

    if (this.ventaMap) {
      this.ventaMap.remove();
      this.ventaMap = null;
    }

    this.ventaMarker = null;
  },

  pintarMapaVenta() {
    const venta = this.selectedMapVenta;
    const el = this.$refs.ventaMap;

    if (!venta || !el) return;

    const lng = Number(venta.x);
    const lat = Number(venta.y);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    if (this.ventaMap) {
      this.ventaMap.remove();
      this.ventaMap = null;
    }

    this.ventaMap = L.map(el, {
      zoomControl: true,
      zoomAnimation: false,
      fadeAnimation: false,
    }).setView([lat, lng], 9);

    const cartoApiKey = process.env.VUE_APP_CARTO_API_KEY;

    L.tileLayer(
      `https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}.png?key=${cartoApiKey}`,
      {
        subdomains: "abcd",
        maxZoom: 20,
        attribution: "© OpenStreetMap, © CARTO",
      }
    ).addTo(this.ventaMap);

    const color = this.colorTecnologia(venta.tecnologia);

    this.ventaMarker = L.circleMarker([lat, lng], {
      radius: 12,
      color: "#ffffff",
      weight: 2,
      fillColor: color,
      fillOpacity: 1,
      className: "single-asset-marker",
    }).addTo(this.ventaMap);

    this.ventaMarker.bindPopup(`
      <div style="min-width: 200px;">
        <b>${venta.nombre || "Activo"}</b><br/>
        <b>Tecnología:</b> ${venta.tecnologia || "—"}<br/>
        <b>MW:</b> ${venta.mw ?? "—"}<br/>
        <b>Municipio:</b> ${venta.municipio || "—"}<br/>
        <b>Provincia:</b> ${venta.provincia || "—"}<br/>
        <b>Precio mercado:</b> ${this.money(venta.precio_banca)}
      </div>
    `).openPopup();

    setTimeout(() => {
      if (this.ventaMap) {
        this.ventaMap.invalidateSize();
        this.ventaMap.setView([lat, lng], 9);
      }
    }, 150);
  },

  colorTecnologia(tecnologia) {
    const t = (tecnologia || "").toString().trim().toLowerCase();

    if (t.includes("solar") || t.includes("fotovolta")) return "#FDB813";
    if (t.includes("eólica") || t.includes("eolica")) return "#248F11";
    if (t.includes("hidro")) return "#4AA3DF";
    if (t.includes("nuclear")) return "#8E44AD";
    if (t.includes("ciclo") || t.includes("gas")) return "#E67E22";

    return "#757575";
  },
    formatDateTime(v) {
  if (!v) return "-";

  const d = new Date(v);

  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString("es-ES", {
    timeZone: "Europe/Madrid",
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
},
    sellerName(v) {
      if (this.isSeller(v)) return "Tú";

      return (
        v.vendedor_nombre ||
        v.vendedor_name ||
        v.vendedor_username ||
        v.vendedor_email ||
        `Jugador ${v.vendedor_id}`
      );
    },
    buyerName(o) {
      return (
        o.comprador_nombre ||
        o.comprador_name ||
        o.comprador_username ||
        o.comprador_email ||
        `Jugador ${o.comprador_id}`
      );
    },
    isSeller(v) {
      return this.meId && String(v.vendedor_id) === String(this.meId);
    },

    canOffer(v) {
      if (v.estado !== "abierta") return false;
      if (this.isSeller(v)) return false;
      return true;
    },

    async loadVentas() {
      this.loading = true;
      this.error = "";

      try {
        const ligaId = this.requireActiveLigaId();

        const res = await axios.get("/api/market/ventas", {
          params: { liga_id: ligaId },
        });

        // No aplicar una respuesta antigua si el usuario cambió de liga
        // mientras la petición estaba en curso.
        if (ligaId !== this.activeLigaId) return;

        this.ventas = (res.data && res.data.data)
          ? res.data.data
          : [];

        this.$emit("sales-count", this.ventas.length);
      } catch (e) {
        this.error = e?.response?.data?.message || "No se pudieron cargar las ventas.";
        this.$emit("sales-count", 0);
      } finally {
        this.loading = false;
      }
    },

    openOffer(v) {
      this.selectedVenta = v;
      this.offerAmount = v.mi_oferta ? Number(v.mi_oferta) : null;
      this.offerError = "";
      this.offerDialog = true;
    },
    closeOffer() {
      this.offerDialog = false;
      this.selectedVenta = null;
      this.offerAmount = null;
      this.offerError = "";
    },

    async submitOffer() {
      if (!this.selectedVenta) return;

      const amount = Number(this.offerAmount || 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        this.offerError = "Introduce un importe válido.";
        return;
      }
      if (amount > Number(this.budget || 0)) {
        this.offerError = "No tienes presupuesto suficiente para esa oferta.";
        return;
      }

      try {
        const ligaId = this.requireActiveLigaId();

        await axios.post("/api/market/anadir-oferta", {
          sale_id: this.selectedVenta.id,
          value: amount,
          liga_id: ligaId,
        });
        this.closeOffer();
        await this.loadVentas();
        this.emitBudgetRefresh();
      } catch (e) {
        this.offerError = e?.response?.data?.message || "No se pudo enviar la oferta.";
      }
    },

    async cancelSale(v) {
      try {
        const ligaId = this.requireActiveLigaId();

        await this.store.cancelSale({
          sale_id: v.id,
          liga_id: ligaId,
        });

        await this.loadVentas();
        this.emitBudgetRefresh();
      } catch (e) {
        this.error =
          e?.response?.data?.message ||
          e?.message ||
          "No se pudo cancelar la venta.";
      }
    },

    async sellToBank(v) {
        try {
          this.error = "";

          const ligaId = this.requireActiveLigaId();

          const res = await axios.post("/api/market/vender-mercado", {
            sale_id: v.id,
            liga_id: ligaId,
          });

          const payload = res?.data?.data || {};
          const dineroRecuperado = payload.precio_banca || v.precio_banca || 0;

          await Promise.all([
            this.loadVentas(),
            this.store.refreshCompany(ligaId),
            this.store.refreshGenerators(ligaId),
            this.store.refreshStats(ligaId),
            this.store.refreshEvents(ligaId),
          ]);

          this.emitBudgetRefresh();

          await Swal.fire({
            icon: "success",
            title: "Vendido a mercado",
            html: `
              <div style="font-size: 15px;">
                Has vendido <b>${v.nombre}</b><br>
                Dinero recuperado: <b>${this.money(dineroRecuperado)}</b>
              </div>
            `,
            confirmButtonText: "Entendido",
            buttonsStyling: false,
            customClass: {
              confirmButton: "swal-electroliga-confirm",
            },
          });
        } catch (e) {
          console.error("Error vender a mercado:", e?.response?.data || e);

          const payload = e?.response?.data;

          this.error =
            payload?.message ||
            payload?.data?.message ||
            payload?.data ||
            "No se pudo vender a mercado.";

          await Swal.fire({
            icon: "error",
            title: "No se pudo vender a mercado",
            text: this.error,
            confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
          });
        }
      },

    async openOffersList(v) {
      this.offersDialog = true;
      this.offersVentaId = v.id;
      this.offersVenta = v;
      this.offers = [];
      this.offersError = "";

      try {
        const ligaId = this.requireActiveLigaId();

        const res = await axios.get("/api/market/ventas/ofertas", {
          params: {
            venta_id: v.id,
            liga_id: ligaId,
          },
        });
        this.offers = (res.data && res.data.data) ? res.data.data : [];
      } catch (e) {
        this.offersError = e?.response?.data?.message || "No se pudieron cargar las ofertas.";
      }
    },

    closeOffersList() {
      this.offersDialog = false;
      this.offersVentaId = null;
      this.offersVenta = null;
      this.offers = [];
      this.offersError = "";
    },

   async acceptOffer(o) {
      try {
        const ventaNombre = this.offersVenta?.nombre || "el activo";
        const comprador = this.buyerName(o);
        const importe = Number(o.importe || 0);

        const confirm = await Swal.fire({
          icon: "warning",
          title: "¿Aceptar oferta?",
          html: `
            <div style="font-size: 15px;">
              Vas a vender <b>${ventaNombre}</b><br>
              Comprador: <b>${comprador}</b><br>
              Importe: <b>${this.money(importe)}</b>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: "Sí, aceptar",
          cancelButtonText: "Cancelar",
          customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
        });

        if (!confirm.isConfirmed) return;

        const ligaId = this.requireActiveLigaId();

        await this.store.acceptOffer({
          offer_id: o.id,
          liga_id: ligaId,
        });

        await this.loadVentas();
        this.emitBudgetRefresh();

        await Swal.fire({
          icon: "success",
          title: "Activo vendido",
          html: `
            <div style="font-size: 15px;">
              Has vendido <b>${ventaNombre}</b><br>
              Comprador: <b>${comprador}</b><br>
              Dinero recibido: <b>${this.money(importe)}</b>
            </div>
          `,
          confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
        });
        this.closeOffersList();
      } catch (e) {
        const payload = e?.response?.data;

        this.offersError =
          payload?.message ||
          payload?.data?.message ||
          payload?.data ||
          "No se pudo aceptar la oferta.";

        await Swal.fire({
          icon: "error",
          title: "No se pudo aceptar la oferta",
          text: this.offersError,
          confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
        });
      }
    },
  },
};
</script>
<style scoped>
.venta-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.map-emoji-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0 2px;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.map-emoji-btn:hover {
  transform: scale(1.2);
  filter: drop-shadow(0 0 4px rgba(49, 99, 95, 0.55));
}

.asset-map-modal {
  width: 100%;
  height: 480px;
  border-radius: 14px;
  overflow: hidden;
  background: #eef2f5;
}

.asset-map-info {
  color: #414141;
  font-size: 13px;
}

:deep(.single-asset-marker) {
  filter:
    drop-shadow(0 0 6px rgba(255, 255, 255, 1))
    drop-shadow(0 0 14px rgba(49, 99, 95, 0.95));
}

.map-close-btn {
  width: 34px !important;
  height: 34px !important;
  min-width: 34px !important;
  background-color: #31635f !important;
  color: white !important;
  transition: all 0.2s ease;
}

.map-close-btn:hover {
  background-color: #244c49 !important;
  transform: scale(1.05);
}

.map-close-x {
  color: white !important;
  font-size: 26px;
  line-height: 1;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-1px);
}
.venta-title-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
}

.history-info-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.history-info-btn:hover {
  transform: scale(1.2);
  filter: drop-shadow(0 0 4px rgba(49, 99, 95, 0.55));
}

.history-info-icon {
  font-size: 18px !important;
  color: #31635f;
}

.modal-history-title {
  font-family: "Orbitron", sans-serif !important;
  color: #31635f;
  font-weight: 700;
}

.asset-history-points {
  font-family: "Orbitron", sans-serif !important;
  font-weight: 700;
}
.sale-price-summary {
  min-width: 125px;
}

.bank-sale-price {
  color: #31635f;
}
/* Botones de acciones del mercado */
.market-action-btn {
  font-family: "Orbitron", sans-serif !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  letter-spacing: 1px !important;
  border-radius: 7px !important;
}

/* Acciones principales: Hacer oferta / Vender a mercado */
.market-action-btn-primary:not(.v-btn--disabled) {
  background-color: #31635f !important;
  border-color: #31635f !important;
  color: white !important;
}

/* Ver ofertas */
.market-action-btn-secondary:not(.v-btn--disabled) {
  color: #31635f !important;
  border-color: #31635f !important;
  background-color: transparent !important;
}

/* Cancelar venta */
.market-action-btn-cancel:not(.v-btn--disabled) {
  color: #c62828 !important;
  border-color: #c62828 !important;
  background-color: transparent !important;
}

/* Hover */
.market-action-btn-primary:not(.v-btn--disabled):hover {
  background-color: #244c49 !important;
}

.market-action-btn-secondary:not(.v-btn--disabled):hover {
  background-color: rgba(49, 99, 95, 0.08) !important;
}

.market-action-btn-cancel:not(.v-btn--disabled):hover {
  background-color: rgba(198, 40, 40, 0.06) !important;
}
</style>
<style>
.swal2-confirm.swal-electroliga-confirm {
  background-color: #31635f !important;
  color: #ffffff !important;
  border: none !important;
  border-radius: 8px !important;

  min-width: 150px;
  height: 44px;
  padding: 0 24px !important;

  font-family: "Orbitron", sans-serif !important;
  font-size: 14px !important;
  font-weight: 600 !important;

  cursor: pointer;
  transition:
    background-color 0.2s ease,
    transform 0.15s ease,
    box-shadow 0.2s ease;
}

.swal2-confirm.swal-electroliga-confirm:hover {
  background-color: #244c49 !important;
  transform: translateY(-1px);
  box-shadow: 0 5px 12px rgba(49, 99, 95, 0.25);
}

.swal2-confirm.swal-electroliga-confirm:focus {
  outline: none !important;
  box-shadow: 0 0 0 3px rgba(49, 99, 95, 0.22) !important;
}
</style>